import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import PlanGrid from './PlanGrid';
import BillingDetailsForm, { BillingDetailsValue } from './BillingDetailsForm';
import { planToSelect } from './planToSelect';
import { autoRenewsOnPurchase, chargePeriodPhrase } from './planExpiry';
import {
  formatFirstChargeDate,
  isActivated,
  isRenewalLive,
  scheduledStageCopy,
  POLL_DELAYS_MS,
} from './activation';
import { higherTierTargets } from './upgradeEligibility';
import { billingApiClient, isBillingConfigured, BillingPlan, BillingProfile } from '../../services/billingApi';
import { entitlementsApi } from '../../services/entitlementsApi';
import { useRazorpayCheckout } from './useRazorpayCheckout';
import { apiErrorDetail } from '../../utils/apiError';
import { chargeSummary, formatPaiseExact, formatPriceInr } from '../../utils/formatPrice';
import { checkoutErrorMessage, DETAILS_ERROR } from './checkoutErrors';

interface UpgradeModalProps {
  /** The workspace ROLE to fetch plans for: 'criminal' | 'civil' | 'corporate'
   *  (types/practiceArea.ts `Role`, which is exactly the engine's vertical set).
   *  NOT a practice-area lens such as 'civil_consumer' — the engine has no SKUs
   *  under that key. Callers read sessionStorage 'nyayabodh_active_role'. */
  vertical: string;
  open: boolean;
  onClose: () => void;
  /** Fired once the poll confirms the purchased tier is live (source=engine_push).
   *  Callers use this to re-run their existing entitlement/usage fetch — there is
   *  no separate global "refresh entitlements" event bus today. */
  onActivated?: () => void;
  /** RENEW an existing no-mandate plan (annual, prepaid monthly): preselects
   *  that plan, and activation waits for the subscription's period end to
   *  move past `currentPeriodEnd` — the tier is already active, so the tier
   *  check alone would report success before the renewal payment landed. */
  renewal?: { planCode: string; currentPeriodEnd: string | null };
  /** UPGRADE an existing ACTIVE subscription to a strictly-higher tier in
   *  the same vertical. Mutually exclusive with `renewal` — never both at
   *  once (PlanSummaryCard only ever opens one modal instance). Kept as its
   *  own prop rather than folded into `renewal`'s shape: `renewal` keys off
   *  re-buying the SAME `planCode`, `upgrade` keys off EXCLUDING the
   *  current tier and anything below it, which is the opposite filter. */
  upgrade?: { subscriptionId: string; currentTier: string };
}

type ModalStage =
  | 'loading'
  | 'grid'
  | 'details'
  | 'empty'
  | 'processing'
  | 'activating'
  | 'scheduled'
  | 'timed_out'
  | 'error';

// Poll schedule and the "is it live yet" predicate live in ./activation so
// they can be unit-tested (this module pulls in Firebase via billingApi,
// which jsdom cannot load). NEVER fake instant activation.

// Plan-specific overrides on top of checkoutErrors.ts's shared mapping —
// these two codes mean something different in THIS flow than their generic
// defaults would say.
const PLAN_ERROR_OVERRIDES: Record<string, string> = {
  plan_not_purchasable: 'This plan is not yet live for purchase.',
  live_subscription_exists: 'You already have an active plan on this account.',
  // Upgrade-only (POST /subscriptions/{id}/upgrade requires status===ACTIVE
  // exactly) — the generic fallback copy implies a payment-gateway problem,
  // which would be misleading for "this subscription itself can't be
  // upgraded right now" (e.g. a failed charge needs Fix payment first).
  subscription_not_active:
    "Your subscription isn't in a state that can be upgraded right now — try Fix payment first if a charge failed.",
};

function errorMessage(err: any): string {
  return checkoutErrorMessage(err, PLAN_ERROR_OVERRIDES);
}

/**
 * Upgrade modal — 2x2 plan grid (pro/max x monthly/annual, annual pre-selected)
 * -> Razorpay Checkout -> honest activation poll against /entitlements/me.
 *
 * When REACT_APP_BILLING_URL is unset (engine not deployed / current build),
 * `isBillingConfigured()` is false and the modal renders a "billing not
 * configured" state instead of attempting network calls that would 404/CORS-fail.
 */
const UpgradeModal: React.FC<UpgradeModalProps> = ({ vertical, open, onClose, onActivated, renewal, upgrade }) => {
  const [stage, setStage] = useState<ModalStage>('loading');
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [selected, setSelected] = useState<BillingPlan | null>(null);
  const [errorText, setErrorText] = useState<string>('');
  // Neutral, non-error guidance (muted, not crimson) — see ondismiss below.
  const [notice, setNotice] = useState<string>('');
  // Human date of a deferred first charge; drives the 'scheduled' stage.
  const [firstChargeLabel, setFirstChargeLabel] = useState<string | null>(null);
  const pollCancelledRef = useRef(false);
  // GST tax invoice details (spec 2026-10-17): asked once, when the engine
  // answers checkout with 409 billing_profile_required.
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string>('');
  const { openCheckout } = useRazorpayCheckout();

  const billingConfigured = isBillingConfigured();

  const loadPlans = useCallback(async () => {
    setStage('loading');
    setErrorText('');
    setNotice('');
    try {
      const data = await billingApiClient.getPlans(vertical);
      // Upgrade mode: only ever offer a strictly-higher tier — never the
      // current one (that's a renewal, not an upgrade) and never a lower
      // one (the engine's /upgrade endpoint has no direction check, so this
      // filter is the only thing standing between a click here and an
      // unimplemented-for-UX downgrade hitting that endpoint).
      const filtered = upgrade ? higherTierTargets({ tier: upgrade.currentTier }, data) : data;
      setPlans(filtered);
      // Preserves an existing selection across a Retry — see planToSelect.
      const renewing = renewal ? filtered.find((p) => p.plan_code === renewal.planCode) : undefined;
      setSelected((current) => renewing ?? planToSelect(filtered, current));
      // An empty catalog used to fall through to 'grid': zero plan cards, a
      // disabled "Continue to payment", and no explanation at all. It happens
      // for real — every seeded SKU starts inactive until an owner prices it,
      // so a fresh environment returns [] for every vertical. In upgrade mode
      // it also covers the (equally real) case of nothing priced ABOVE the
      // lawyer's current tier yet, even though their own tier is on sale.
      setStage(filtered.length === 0 ? 'empty' : 'grid');
    } catch (err) {
      console.error('Failed to load plans:', err);
      setErrorText('Could not load pricing — please try again.');
      setStage('error');
    }
  }, [vertical, renewal, upgrade]);

  useEffect(() => {
    if (!open) return;
    pollCancelledRef.current = false;
    if (!billingConfigured) {
      setStage('error');
      setErrorText('Billing is not configured yet — please check back soon.');
      return;
    }
    loadPlans();
    return () => {
      pollCancelledRef.current = true;
    };
  }, [open, billingConfigured, loadPlans]);

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  /** Poll /entitlements/me with backoff until the purchased tier shows
   *  source=engine_push, or the schedule is exhausted (honest timeout state —
   *  never fake instant activation). */
  const pollForActivation = useCallback(
    async (plan: BillingPlan) => {
      setStage('activating');
      for (const delay of POLL_DELAYS_MS) {
        await sleep(delay);
        if (pollCancelledRef.current) return;
        try {
          if (renewal) {
            const [subs, entitlements] = await Promise.all([
              billingApiClient.getMySubscriptions(),
              entitlementsApi.getMine(),
            ]);
            if (isRenewalLive(subs, entitlements, vertical, renewal, plan.tier)) {
              if (pollCancelledRef.current) return;
              toast.success('Your plan is renewed.');
              onActivated?.();
              onClose();
              return;
            }
            continue;
          }
          const entitlements = await entitlementsApi.getMine();
          if (isActivated(entitlements, { vertical, tier: plan.tier })) {
            if (pollCancelledRef.current) return;
            toast.success('Your plan is active.');
            onActivated?.();
            onClose();
            return;
          }
        } catch (err) {
          // Transient errors during polling are swallowed — we keep polling
          // on the same schedule rather than surfacing noise mid-poll.
          console.error('Entitlement poll failed:', err);
        }
      }
      if (!pollCancelledRef.current) {
        setStage('timed_out');
      }
    },
    [vertical, onActivated, onClose, renewal],
  );

  const handleContinueToPayment = async () => {
    if (!selected) return;
    setStage('processing');
    setErrorText('');
    setNotice('');
    try {
      const session = upgrade
        ? await billingApiClient.upgradeSubscription(upgrade.subscriptionId, selected.plan_code)
        : await billingApiClient.createCheckout(selected.plan_code);

      // A deferred first charge (monthly conversion mid-trial, or an
      // upgrade's free-time credit) activates only at Razorpay `start_at` —
      // days away — and nothing is pushed until then. Polling for activation
      // would time out and claim "activation in progress" when NOTHING has
      // been charged. Such a checkout gets the 'scheduled' stage instead.
      const deferredLabel = formatFirstChargeDate(session.first_charge_at);

      await openCheckout(session.checkout_payload || {}, {
        onPaid: () => {
          // Razorpay's client-side handler. The authoritative state change
          // still arrives via webhook -> engine -> push, so this never grants
          // anything itself. For a deferred charge it only confirms the
          // mandate was set up — access is unchanged, their trial continues.
          if (deferredLabel) {
            setFirstChargeLabel(deferredLabel);
            setStage('scheduled');
            return;
          }
          pollForActivation(selected);
        },
        onDismiss: () => {
          // A dismissed modal is NOT proof that no payment happened: a UPI
          // app-switch can complete the payment and lose the handler
          // callback. Returning silently to the grid invited paying twice.
          // (A second attempt now replays the SAME Razorpay object, so it
          // cannot double-charge — but it should not look like the first
          // one failed, either.)
          setNotice(
            'If you completed the payment, your plan will activate within a couple of ' +
              'minutes — you do not need to pay again.',
          );
          setStage('grid');
        },
        onFailed: () => {
          toast.error('Payment failed — please try again.');
          setStage('grid');
        },
      });
    } catch (err) {
      if (apiErrorDetail(err)?.code === 'billing_profile_required') {
        // Not an error: the invoice needs the buyer's details first.
        try {
          setProfile(await billingApiClient.getBillingProfile());
          setDetailsError('');
          setStage('details');
          return;
        } catch (profileErr) {
          console.error('Billing profile load failed:', profileErr);
        }
      }
      console.error('Checkout failed:', err);
      setErrorText(errorMessage(err));
      setStage('grid');
      toast.error(errorMessage(err));
    }
  };

  const handleSaveDetails = async (value: BillingDetailsValue) => {
    setSavingDetails(true);
    setDetailsError('');
    try {
      await billingApiClient.saveBillingProfile({
        billing_name: value.billing_name.trim(),
        state_code: value.state_code,
        gstin: value.gstin.trim() || null,
        billing_address: value.billing_address.trim() || null,
      });
      setSavingDetails(false);
      await handleContinueToPayment();
    } catch (err) {
      setSavingDetails(false);
      const code = apiErrorDetail(err)?.code;
      setDetailsError((code && DETAILS_ERROR[code]) || 'Could not save your details. Please try again.');
    }
  };

  const handleClose = () => {
    pollCancelledRef.current = true;
    onClose();
  };

  // Price clause for the 'scheduled' stage's first-charge sentence, e.g.
  // " of ₹2,999 (incl. GST)" — built here (currency formatting), handed to
  // scheduledStageCopy as a plain string (pure wording decision only).
  const priceClause = selected
    ? ` of ${
        selected.total_inr !== undefined
          ? `${formatPaiseExact(selected.total_inr)} (incl. GST)`
          : `${formatPriceInr(selected.price_inr, selected.billing_period)} + GST`
      }`
    : '';
  const scheduledCopy = useMemo(
    () => scheduledStageCopy({ upgrade: !!upgrade, firstChargeLabel, priceClause }),
    [upgrade, firstChargeLabel, priceClause],
  );

  const title = useMemo(() => {
    if (stage === 'activating') return 'Activating your plan';
    if (stage === 'timed_out') return 'Payment received';
    if (stage === 'scheduled') return scheduledCopy.title;
    if (stage === 'details') return 'Invoice details';
    return 'Upgrade your plan';
  }, [stage, scheduledCopy]);

  return (
    <Modal open={open} onClose={handleClose} title={title} size="lg">
      {stage === 'loading' && (
        <div className="py-10 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brass" />
        </div>
      )}

      {stage === 'error' && (
        <div className="py-6 text-center space-y-4">
          <p className="text-sm text-parchment-muted-warm">{errorText}</p>
          {billingConfigured && (
            <Button variant="brass" size="sm" onClick={loadPlans}>
              Retry
            </Button>
          )}
        </div>
      )}

      {stage === 'empty' && (
        <div className="py-6 text-center space-y-4">
          <p className="text-sm text-parchment-muted-warm">
            {upgrade
              ? "There's nothing priced above your current plan yet. Please check back soon."
              : 'Plans for this workspace are not open for purchase yet. Please check back soon.'}
          </p>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Close
          </Button>
        </div>
      )}

      {stage === 'grid' && (
        <div className="space-y-5">
          <PlanGrid plans={plans} selected={selected} onSelect={setSelected} />
          {selected && (
            <div className="space-y-1">
              <p className="text-sm text-chamber-panel">
                You&apos;ll pay <span className="font-plex tabular-nums">{chargeSummary(selected)}</span>
                {chargePeriodPhrase(selected)}
              </p>
              {!autoRenewsOnPurchase(selected) && (
                <p className="text-xs text-parchment-muted-warm">
                  {renewal
                    ? 'This payment starts a new period today, replacing any time left on your current one.'
                    : 'Nothing is charged automatically. You can renew from your Profile before it ends.'}
                </p>
              )}
            </div>
          )}
          {notice && <p className="text-sm text-parchment-muted-warm">{notice}</p>}
          {errorText && <p className="text-sm text-crimson-600">{errorText}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t border-parchment-border">
            <Button variant="ghost" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="brass" size="sm" disabled={!selected} onClick={handleContinueToPayment}>
              Continue to payment
            </Button>
          </div>
        </div>
      )}

      {stage === 'details' && profile && (
        <BillingDetailsForm
          states={profile.states}
          initial={{
            billing_name: profile.billing_name ?? '',
            state_code: profile.state_code ?? '',
            gstin: profile.gstin ?? '',
            billing_address: profile.billing_address ?? '',
          }}
          saving={savingDetails}
          error={detailsError}
          onSubmit={handleSaveDetails}
          onCancel={() => setStage('grid')}
        />
      )}

      {stage === 'processing' && (
        <div className="py-10 flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brass" />
          <p className="text-sm text-parchment-muted-warm">Opening secure checkout…</p>
        </div>
      )}

      {stage === 'activating' && (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brass" />
          <p className="text-sm text-chamber-panel font-medium">Payment received — activating…</p>
          <p className="text-xs text-parchment-muted-warm max-w-sm">
            This usually takes a few seconds. Please keep this window open.
          </p>
        </div>
      )}

      {stage === 'scheduled' && (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-chamber-panel font-medium">{scheduledCopy.headline}</p>
          <p className="text-xs text-parchment-muted-warm max-w-sm">{scheduledCopy.body}</p>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Close
          </Button>
        </div>
      )}

      {stage === 'timed_out' && (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-chamber-panel font-medium">Payment received, activation in progress.</p>
          <p className="text-xs text-parchment-muted-warm max-w-sm">
            This can take a minute — your access updates automatically. You can safely close this
            window and keep working; refresh later if you don&apos;t see the change.
          </p>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default UpgradeModal;
